import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { formatFltPersons } from './proposal';
import {
  buildTripPreviewModel,
  resolveLanguageFromProposal,
  resolveTripPreviewActiveFromInputData,
  TripPreviewModel,
} from './tripResultBuilderPreview';
import { dateFormat, htmlToBase64 } from './utils';

export type TripClientSummaryData = {
  startDate: string | null;
  endDate: string | null;
  travelDatesLabel: string | null;
  heroDatesLabel: string | null;
  nights: number | null;
  travellersLabel: string | null;
  heroTravellersLabel: string | null;
  destinations: string[];
  destinationsLabel: string | null;
  heroDestinationsLabel: string | null;
  quoteValidLabel: string | null;
  title: string;
  themeLabel: string | null;
  flightLabel: string | null;
};

const toDayKey = (value?: string, locale?: string) =>
  value ? dateFormat(value, 'yyyy-MM-dd', locale) : '';

const formatHotelPersons = (
  pax: { adults?: number; children?: number; infant?: number; youth?: number; senior?: number },
  t: (key: string) => string,
) => {
  const parts: string[] = [];

  if (pax.adults) {
    parts.push(`${pax.adults} ${pax.adults > 1 ? t('search.adults') : t('search.adult')}`);
  }
  if (pax.children) {
    parts.push(`${pax.children} ${pax.children > 1 ? t('search.children') : t('search.child')}`);
  }
  if (pax.youth) {
    parts.push(`${pax.youth} ${pax.youth > 1 ? t('search.youths') : t('search.youth')}`);
  }
  if (pax.senior) {
    parts.push(`${pax.senior} ${pax.senior > 1 ? t('search.seniors') : t('search.senior')}`);
  }
  if (pax.infant) {
    parts.push(`${pax.infant} ${pax.infant > 1 ? t('search.infants') : t('search.infant')}`);
  }

  return parts.join(', ') || null;
};

const formatHeroDates = (startDate: string, endDate: string, locale: string) => {
  const start = new Date(`${toDayKey(startDate, locale)}T00:00:00`);
  const end = new Date(`${toDayKey(endDate, locale)}T00:00:00`);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${dateFormat(startDate, 'd', locale)}–${dateFormat(endDate, 'd MMMM yyyy', locale)}`;
  }

  return `${dateFormat(startDate, 'd MMMM', locale)} – ${dateFormat(endDate, 'd MMMM yyyy', locale)}`;
};

const formatHeroTravellers = (label: string) =>
  label.replace(/,\s*/g, ' · ').replace(/\b\w/g, (char) => char.toUpperCase());

const getDestinations = (model: TripPreviewModel) => {
  const cities: string[] = [];
  model.slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      if (node.city && cities[cities.length - 1] !== node.city) {
        cities.push(node.city);
      }
    });
  });
  return cities;
};

const getFlightLabel = (model: TripPreviewModel) => {
  const firstFlight =
    model.slots[0]?.beforeSection?.flights.find((flight) => flight.isActive) ||
    model.slots[0]?.beforeSection?.flights[0];

  if (!firstFlight) {
    return null;
  }

  const flightNumber = firstFlight.flightInfoLabel?.split(' · ')[0]?.trim();
  if (firstFlight.airlineCode && flightNumber) {
    return `${firstFlight.airlineCode} ${flightNumber}`;
  }

  return firstFlight.flightInfoLabel || firstFlight.airlineCode || null;
};

const getThemeLabel = (proposal: ProposalData) => {
  const note = (proposal.note || '').replace(/<[^>]*>/g, '').trim();
  if (note) {
    return note;
  }

  const hotels = (proposal.listOfProducts || []).flatMap((product) =>
    product.listOfItems.flatMap((item) => item.hotels || []),
  );
  const displayNote = hotels[0]?.displayNote?.name?.replace(/<[^>]*>/g, '').trim();
  return displayNote || null;
};

export const parseHeroTitle = (title: string, destinations: string[]) => {
  const words = title.trim().split(/\s+/).filter(Boolean);

  if (words.length >= 3) {
    const mid = Math.floor(words.length / 2);
    return {
      before: words.slice(0, mid).join(' '),
      accent: words[mid],
      after: words.slice(mid + 1).join(' '),
    };
  }

  if (words.length === 2) {
    return {
      before: words[0],
      accent: words[1],
      after: 'Journey',
    };
  }

  return {
    before: title,
    accent: destinations[0] || '',
    after: destinations.length ? 'Journey' : '',
  };
};

export const buildTripClientSummary = (
  proposal: ProposalData,
  t: (key: string, options?: { defaultValue?: string }) => string,
): TripClientSummaryData => {
  const locale = resolveLanguageFromProposal(proposal.externalParam_1 || '');
  const hotels = (proposal.listOfProducts || []).flatMap((product) =>
    product.listOfItems.flatMap((item) => item.hotels || []),
  );
  const flights = (proposal.listOfProducts || []).flatMap((product) =>
    product.listOfItems.flatMap((item) => item.flights || []),
  );

  const checkIns = hotels.map((hotel) => hotel.rooms?.[0]?.startDate).filter(Boolean) as string[];
  const checkOuts = hotels.map((hotel) => hotel.rooms?.[0]?.endtDate).filter(Boolean) as string[];
  const startDate = [...checkIns].sort()[0] || null;
  const endDate = [...checkOuts].sort().slice(-1)[0] || null;

  const travelDatesLabel =
    startDate && endDate
      ? `${dateFormat(startDate, 'dd MMM yyyy', locale)} – ${dateFormat(endDate, 'dd MMM yyyy', locale)}`
      : null;

  const heroDatesLabel = startDate && endDate ? formatHeroDates(startDate, endDate, locale) : null;

  const nights =
    startDate && endDate
      ? Math.max(
          0,
          Math.round(
            (new Date(`${toDayKey(endDate, locale)}T00:00:00`).getTime() -
              new Date(`${toDayKey(startDate, locale)}T00:00:00`).getTime()) /
              (1000 * 60 * 60 * 24),
          ),
        )
      : null;

  const travellersLabel =
    (flights[0] && formatFltPersons(flights[0], t)) ||
    (hotels[0]?.rooms?.[0]?.pax && formatHotelPersons(hotels[0].rooms[0].pax, t)) ||
    null;

  const model = buildTripPreviewModel(proposal, resolveTripPreviewActiveFromInputData(proposal));
  const destinations = getDestinations(model);
  const destinationsCount = destinations.length;
  const validNights = nights && nights > 0 ? nights : null;

  const heroDestinationsLabel =
    destinationsCount || validNights
      ? [
          destinationsCount
            ? `${destinationsCount} ${
                destinationsCount > 1
                  ? t('trip_builder.destinations', { defaultValue: 'Destinations' })
                  : t('trip_builder.destination', { defaultValue: 'Destination' })
              }`
            : null,
          validNights
            ? `${validNights} ${validNights > 1 ? t('hotel.nights') : t('hotel.night')}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return {
    startDate,
    endDate,
    travelDatesLabel,
    heroDatesLabel,
    nights: validNights,
    travellersLabel,
    heroTravellersLabel: travellersLabel ? formatHeroTravellers(travellersLabel) : null,
    destinations,
    destinationsLabel: destinations.length ? destinations.join(' · ') : null,
    heroDestinationsLabel,
    quoteValidLabel: proposal.dateC
      ? dateFormat(`${proposal.dateC}Z`, 'dd MMM yyyy', locale)
      : null,
    title: proposal.description || t('trip_builder.proposal', { defaultValue: 'Proposal' }),
    themeLabel: getThemeLabel(proposal),
    flightLabel: getFlightLabel(model),
  };
};

export const getTripBuilderPdfBody = (container: HTMLElement | null) => {
  const rootEl = container?.querySelector('.tbp-root');
  const rootClone = rootEl?.cloneNode(true) as HTMLElement | undefined;
  rootClone?.querySelectorAll('img').forEach((img) => {
    if (!img.closest('.tbp-route-map')) {
      img.remove();
    }
  });
  const stylesHtml = Array.from(document.querySelectorAll('style'))
    .filter((el) => el.textContent?.includes('.tbp-root'))
    .map((el) => `<style>${el.textContent}</style>`)
    .join('');
  const bodyHtml = `${stylesHtml}${rootClone?.outerHTML || ''}`;
  const newBodyHtml = bodyHtml.replaceAll(
    /https:\/\/[^/]+\/backend\/s3/g,
    'https://s3-eu-west-1.amazonaws.com/',
  );

  return {
    printHtml: htmlToBase64(newBodyHtml || ''),
    header: htmlToBase64(''),
    footer: htmlToBase64(''),
  };
};

export type TripBuilderClientUiVersion = 1 | 2;

export const resolveTripBuilderClientUiVersion = (
  companyId: number,
): TripBuilderClientUiVersion => {
  try {
    return companyId === 108 ? 2 : 1;
  } catch {
    return 1;
  }
};

export const getActiveTripPreviewHotelNames = (model: TripPreviewModel): string[] =>
  model.slots.flatMap((slot) =>
    slot.chainNodes.flatMap((node) => {
      const hotel = node.hotelSection?.hotels.find((item) => item.isActive);
      const bpid = hotel?.bpid?.trim();
      const city = node.city?.trim();
      return bpid && city ? [`${bpid}~${city}`] : [];
    }),
  );
